// Hooks
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  startTransition,
} from "react";
import { useMedplum } from "@medplum/react";

// Types
import type { Task } from "@medplum/fhirtypes";

// Services
import { buildNotificationTaskSearchParams } from "@benchmark/core/services";
import type { MedplumClient } from "@medplum/core";

// Constants / utils
import {
  NOTIFICATION_TYPE_DEFINITIONS,
  hasNotificationTaskCode,
} from "@benchmark/core/constants";
import { getResourceIdFromReference } from "@benchmark/core/utils";

// Events
import { DomainEventNames } from "@benchmark/core/events";
import { useEventDrivenRefetch } from "@benchmark/core/hooks";

const NOTIFICATION_PAGE_SIZE = 25;

const SERVICE_APPROVAL_CODE =
  NOTIFICATION_TYPE_DEFINITIONS.SERVICE_APPROVAL.code;

/**
 * Read-time reconciliation (D9/D12): a service-approval notice is the viewer's
 * only while its focus approval task is `ready` and owned by them. Drop notices
 * whose focus task has resolved (missed/failed clear) or whose ownership moved
 * (a reassignment path that did not update the notice) — so a stale PHI notice
 * never shows to the wrong advocate. Clinical-risk notices pass through untouched.
 */
export const reconcileServiceApprovalNotices = async (
  client: MedplumClient,
  notices: Task[],
  ownerRefs: Set<string>,
): Promise<Task[]> => {
  const serviceApproval = notices.filter((task) =>
    hasNotificationTaskCode(task, SERVICE_APPROVAL_CODE),
  );
  if (serviceApproval.length === 0) return notices;

  const focusIds = [
    ...new Set(
      serviceApproval
        .map((task) =>
          getResourceIdFromReference(task.focus?.reference, "Task"),
        )
        .filter((id): id is string => Boolean(id)),
    ),
  ];
  const focusById = new Map<string, Task>();
  if (focusIds.length > 0) {
    const focusTasks = await client.searchResources("Task", {
      _id: focusIds.join(","),
      _count: String(focusIds.length),
      _elements: "id,status,owner",
    });
    for (const task of focusTasks) {
      if (task.id) focusById.set(task.id, task);
    }
  }

  return notices.filter((task) => {
    if (!hasNotificationTaskCode(task, SERVICE_APPROVAL_CODE)) return true;
    const focusId = getResourceIdFromReference(task.focus?.reference, "Task");
    const focusTask = focusId ? focusById.get(focusId) : undefined;
    const focusOwner = focusTask?.owner?.reference;
    return (
      focusTask?.status === "ready" &&
      Boolean(focusOwner) &&
      ownerRefs.has(focusOwner as string)
    );
  });
};

/**
 * Light polling interval for cross-session freshness. In-browser domain events
 * only fire for same-tab writes, but notifications are created server-side
 * (`apps/api`/future Bot), so the open drawer must poll to reflect them (D12
 * fallback until a Medplum WebSocket subscription lands).
 */
const DEFAULT_POLL_INTERVAL_MS = 60_000;

/** Notification center tab. @agentic connections */
export type NotificationTab = "mine" | "queue";

/**
 * Options for {@link useNotificationTasks}.
 * @agentic connections
 */
export type UseNotificationTasksOptions = {
  tab: NotificationTab;
  /** Current user's PractitionerRole ids (used for the "mine" tab). */
  ownerRoleIds: string[];
  enabled?: boolean;
  pollIntervalMs?: number;
};

/**
 * Lists clinical notification Tasks for the notification center: the current
 * user's own notifications ("mine") or the unowned team fallback queue
 * ("queue"). Never returns operational Tasks (D5).
 * @agentic connections
 */
export function useNotificationTasks({
  tab,
  ownerRoleIds,
  enabled = true,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
}: UseNotificationTasksOptions): {
  tasks: Task[];
  loading: boolean;
  error: Error | null;
  /** True when older notifications exist beyond the loaded window. */
  hasMore: boolean;
  /** Extend the newest-first window by one page (no-op while one is loading). */
  loadMore: () => void;
  refetch: () => void;
} {
  // Stable client ref; avoids re-fetching on client identity churn.
  const medplum = useMedplum();
  const medplumRef = useRef(medplum);
  useEffect(() => {
    medplumRef.current = medplum;
  }, [medplum]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const ownerParam = ownerRoleIds
    .map((id) => `PractitionerRole/${id}`)
    .join(",");
  // "mine" needs at least one role; "queue" is always resolvable.
  const active = enabled && (tab === "queue" || ownerRoleIds.length > 0);

  // Paging and results are keyed to the tab/owner scope so a tab switch starts
  // back at page one and never shows the other tab's list while loading.
  const scopeKey = `${tab}|${ownerParam}`;
  const [paging, setPaging] = useState({ scopeKey, pages: 1 });
  const pages = paging.scopeKey === scopeKey ? paging.pages : 1;
  const [view, setView] = useState({
    scopeKey: "",
    pages: 0,
    tasks: [] as Task[],
    hasMore: false,
  });
  const current =
    active && view.scopeKey === scopeKey
      ? view
      : { scopeKey, pages: 0, tasks: [] as Task[], hasMore: false };

  useEffect(() => {
    if (!active) return;

    let cancelled = false;
    startTransition(() => {
      setLoading(true);
      setError(null);
    });

    // ponytail: every load/poll refetches the whole newest-first window from
    // offset 0 (one query, `pages * NOTIFICATION_PAGE_SIZE`), so new arrivals
    // never shift or duplicate rows the way offset paging would. Ceiling: the
    // server's max `_count` (Medplum default 1000); move to `_cursor` paging if
    // anyone needs to scroll that far back.
    const count = pages * NOTIFICATION_PAGE_SIZE;
    const ownerScope: Record<string, string> =
      tab === "queue" ? { "owner:missing": "true" } : { owner: ownerParam };

    const run = async (): Promise<{ tasks: Task[]; hasMore: boolean }> => {
      const client = medplumRef.current;
      const fetched = await client.searchResources("Task", {
        ...buildNotificationTaskSearchParams(),
        _sort: "-authored-on",
        _count: String(count),
        ...ownerScope,
      });
      const hasMore = fetched.length === count;

      if (tab !== "mine") {
        // Team queue (unowned notices). Service-approval notices are always
        // owner-scoped (D1) and must never surface in the shared queue —
        // fail-closed against a crafted unowned notice leaking member PHI to
        // every staff member.
        return {
          tasks: fetched.filter(
            (task) => !hasNotificationTaskCode(task, SERVICE_APPROVAL_CODE),
          ),
          hasMore,
        };
      }

      // Reconciliation failure is type-asymmetric: clinical-risk fails OPEN (a
      // safety notice must never be hidden by a failed read — D2), but
      // service-approval fails CLOSED (never show a PHI notice whose current
      // ownership could not be verified — D1). So on error, keep only
      // non-service-approval notices.
      const ownerRefs = new Set(ownerParam.split(",").filter(Boolean));
      try {
        return {
          tasks: await reconcileServiceApprovalNotices(
            client,
            fetched,
            ownerRefs,
          ),
          hasMore,
        };
      } catch {
        return {
          tasks: fetched.filter(
            (task) => !hasNotificationTaskCode(task, SERVICE_APPROVAL_CODE),
          ),
          hasMore,
        };
      }
    };

    run()
      .then((result) => {
        if (!cancelled)
          startTransition(() => {
            setView({ scopeKey, pages, ...result });
            // A window emptied by service-approval filtering would render the
            // "No notifications" state with no load-more control, hiding older
            // (possibly acute-risk) notices — keep extending until a row shows.
            if (result.tasks.length === 0 && result.hasMore)
              setPaging({ scopeKey, pages: pages + 1 });
          });
      })
      .catch((err: Error) => {
        if (!cancelled)
          startTransition(() => {
            setError(err);
            // A failed poll/loadMore keeps the clinical-risk rows already on
            // screen (a safety notice must not vanish because one refresh
            // failed — D2) but drops service-approval rows, whose ownership can
            // no longer be re-verified (D1 fail-closed). `pages` still advances
            // so the next loadMore retries (the window refetches from offset 0,
            // so a skipped page leaves no gap).
            setView((prev) =>
              prev.scopeKey === scopeKey
                ? {
                    ...prev,
                    pages,
                    tasks: prev.tasks.filter(
                      (task) =>
                        !hasNotificationTaskCode(task, SERVICE_APPROVAL_CODE),
                    ),
                  }
                : { scopeKey, pages, tasks: [], hasMore: false },
            );
          });
      })
      .finally(() => {
        if (!cancelled) startTransition(() => setLoading(false));
      });

    return () => {
      cancelled = true;
    };
  }, [tab, ownerParam, scopeKey, pages, active, reloadKey]);

  // Only extend once the current window has landed, so a scroll handler firing
  // repeatedly at the bottom requests one page at a time.
  const loadMore = () => {
    if (!current.hasMore || current.pages !== pages) return;
    setPaging({ scopeKey, pages: pages + 1 });
  };

  const triggerReload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEventDrivenRefetch(
    [
      DomainEventNames.NotificationTaskCreated,
      DomainEventNames.NotificationTaskUpdated,
    ],
    { refetch: triggerReload, enabled: active },
  );

  // Light polling: server-created notifications never emit an in-browser event,
  // so the open list must poll to stay fresh cross-session (parity with
  // useNotificationTaskCount).
  useEffect(() => {
    if (!active) return;
    const id = setInterval(triggerReload, pollIntervalMs);
    return () => clearInterval(id);
  }, [active, pollIntervalMs, triggerReload]);

  return {
    tasks: current.tasks,
    loading,
    error,
    hasMore: current.hasMore,
    loadMore,
    refetch: triggerReload,
  };
}
